import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { email } from '../utils/generalData.jsx';
import StyledButton from "../components/ui/StyledButton";
import PageMeta from '../components/PageMeta';
import { media } from '../styles/breakpoints';

// Styled components remain the same
const Terminal = styled.div`
    background-color: rgba(0, 0, 0, 0.7);
    border: 1px solid var(--primary-color);
    padding: 30px;
    position: relative;
    font-family: inherit;
    margin-bottom: 50px;
    width: 100%;
    box-sizing: border-box;

    ${media.sm} {
        padding: 15px;
        padding-top: 20px;
    }

    &:before {
        content: "";
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 25px;
        background-color: var(--primary-color);
        opacity: 0.8;
    }

    &:after {
        content: "CONTACT.SYS";
        position: absolute;
        top: 5px;
        left: 10px;
        font-size: 12px;
        font-weight: bold;
        color: var(--background-color);
        text-transform: uppercase;
        letter-spacing: 1px;

        ${media.sm} {
            font-size: 10px;
        }
    }
`;

const TerminalHeader = styled.div`
    position: absolute;
    top: 5px;
    right: 10px;
    display: flex;
    gap: 5px;

    &:before, &:after {
        content: "";
        display: block;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        background-color: rgba(0, 0, 0, 0.3);
    }
`;

const TerminalContent = styled.div`
    margin-top: 20px;
    padding-top: 10px;
`;

const CommandLine = styled.div`
    margin-bottom: 15px;
    display: flex;
    flex-wrap: wrap;

    ${media.sm} {
        flex-direction: column;
    }
`;

const Prompt = styled.span`
    color: var(--primary-color);
    margin-right: 10px;
    white-space: nowrap;
    flex-shrink: 0;

    ${media.sm} {
        font-size: 12px;
        margin-bottom: 4px;
    }
`;

const Command = styled.span`
    color: var(--text-color);
    word-break: break-word;

    ${media.sm} {
        font-size: 13px;
    }
`;

const Response = styled.div`
    margin-left: 20px;
    margin-bottom: 20px;
    color: var(--text-color);
    opacity: 0.8;
    word-break: break-word;

    ${media.sm} {
        margin-left: 0;
        font-size: 13px;
    }
`;

const Form = styled.form`
    margin-top: 30px;
`;

const InputLine = styled.div`
    display: flex;
    margin-bottom: 20px;
    position: relative;

    ${media.sm} {
        flex-direction: column;
        align-items: flex-start;
    }
`;

const InputPrompt = styled.label`
    color: var(--primary-color);
    margin-right: 10px;
    white-space: nowrap;
    flex-shrink: 0;

    ${media.sm} {
        margin-bottom: 6px;
        font-size: 12px;
    }
`;

const InputField = styled.input`
    background-color: transparent;
    border: none;
    border-bottom: 2px solid transparent;
    color: var(--text-color);
    font-family: inherit;
    font-size: 16px;
    width: 100%;
    outline: none;
    padding: 0 0 2px 0;
    caret-color: var(--primary-color);
    transition: border-color 0.2s ease;
    min-width: 0;

    ${media.sm} {
        font-size: 14px;
        border-bottom: 1px solid rgba(255, 51, 153, 0.3);
    }

    &:focus-visible {
        border-bottom-color: var(--primary-color);
        box-shadow: none;
    }
`;

const TextareaField = styled.textarea`
    background-color: transparent;
    border: 1px solid transparent;
    color: var(--text-color);
    font-family: inherit;
    font-size: 16px;
    width: 100%;
    min-height: 100px;
    outline: none;
    padding: 4px;
    resize: vertical;
    caret-color: var(--primary-color);
    transition: border-color 0.2s ease;
    min-width: 0;

    ${media.sm} {
        font-size: 14px;
        border: 1px solid rgba(255, 51, 153, 0.3);
        min-height: 80px;
    }

    &:focus-visible {
        border-color: var(--primary-color);
        box-shadow: none;
    }
`;

const StatusMessage = styled.div`
    margin-top: 20px;
    padding: 10px;
    border: 1px dashed var(--primary-color);
    color: ${props => props.error ? '#ff4d4d' : '#4dff4d'};
    display: ${props => props.visible ? 'block' : 'none'};
    word-break: break-word;

    ${media.sm} {
        font-size: 12px;
        padding: 8px;
    }
`;

const ContactPage = () => {
    const [name, setName] = useState('');
    const [userEmail, setUserEmail] = useState('');
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState({ show: false, error: false, message: '' });
    const [submitting, setSubmitting] = useState(false);

    // History to show completed commands and responses
    const [history, setHistory] = useState([]);

    // Current text being displayed
    const [currentText, setCurrentText] = useState('');
    const [showResponse, setShowResponse] = useState(false);
    const [showForm, setShowForm] = useState(false);

    // Refs for direct control
    const textRef = useRef('');
    const timeoutRef = useRef(null);
    const commandIndexRef = useRef(0);
    const charIndexRef = useRef(0);
    const nameInputRef = useRef(null);

    // Sequence definitions
    const commandSequence = [
        'initialize contact protocol',
        'loading user interface',
        'system ready'
    ];

    const responseSequence = [
        'Contact protocol v5.4.62 initialized.',
        'Interface components loaded. Awaiting input.',
        `All systems operational. Begin transmission to ${email} when ready.`
    ];

    // Timing settings
    const typingSpeed = 50; // ms per character
    const responseDelay = 300; // ms before showing response
    const nextCommandDelay = 500; // ms before starting next command

    // Web3Forms access key from environment
    const accessKey = import.meta.env.VITE_WEB3FORMS_ACCESS_KEY;

    // Start the animation on mount
    useEffect(() => {
        startTyping();

        // Cleanup timeouts on unmount
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // Pure JavaScript typing function
    const startTyping = () => {
        const currentCommandIndex = commandIndexRef.current; // Save current index
        const currentCommand = commandSequence[currentCommandIndex];

        // Type one character at a time
        const typeNextChar = () => {
            if (charIndexRef.current < currentCommand.length) {
                // Add next character
                textRef.current = textRef.current + currentCommand[charIndexRef.current];
                setCurrentText(textRef.current);
                charIndexRef.current++;

                // Schedule next character
                timeoutRef.current = setTimeout(typeNextChar, typingSpeed);
            } else {
                // Command typing complete, show response
                timeoutRef.current = setTimeout(() => {
                    // Show response for current command
                    setShowResponse(true);

                    // Schedule next command or show form
                    timeoutRef.current = setTimeout(() => {
                        // Add current command and response to history
                        // Important: Use saved index to get correct response
                        setHistory(prev => [
                            ...prev,
                            {
                                command: currentCommand,
                                response: responseSequence[currentCommandIndex]
                            }
                        ]);

                        // Reset current display
                        setCurrentText('');
                        setShowResponse(false);
                        textRef.current = '';
                        charIndexRef.current = 0;

                        // Move to next command or show form
                        if (currentCommandIndex < commandSequence.length - 1) {
                            commandIndexRef.current = currentCommandIndex + 1;
                            startTyping();
                        } else {
                            setShowForm(true);
                        }
                    }, nextCommandDelay);
                }, responseDelay);
            }
        };

        // Start typing the first character
        typeNextChar();
    };

    // Focus name input when form appears
    useEffect(() => {
        if (showForm && nameInputRef.current) {
            nameInputRef.current.focus();
        }
    }, [showForm]);

    // Handle form submission with Web3Forms
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Basic validation
        if (!name || !userEmail || !message) {
            setStatus({
                show: true,
                error: true,
                message: 'ERROR: All fields must be completed for transmission.'
            });
            return;
        }

        // Very basic email validation
        if (!userEmail.includes('@') || !userEmail.includes('.')) {
            setStatus({
                show: true,
                error: true,
                message: 'ERROR: Invalid email format detected.'
            });
            return;
        }

        // Set submitting state to show loading
        setSubmitting(true);
        setStatus({
            show: true,
            error: false,
            message: 'PROCESSING: Transmitting message...'
        });

        try {
            // Prepare form data for Web3Forms
            const formData = new FormData();
            formData.append('access_key', accessKey);
            formData.append('name', name);
            formData.append('email', userEmail);
            formData.append('message', message);
            formData.append('subject', 'New Contact Form Submission from DEV_SYSTEM');

            // Additional metadata
            formData.append('from_name', 'DEV_SYSTEM Contact Form');

            // Submit to Web3Forms API
            const response = await fetch('https://api.web3forms.com/submit', {
                method: 'POST',
                body: formData
            });

            const data = await response.json();

            if (data.success) {
                // Success message
                setStatus({
                    show: true,
                    error: false,
                    message: `TRANSMISSION SUCCESSFUL: Message received. Expect response from ${email} within 48 hours.`
                });

                // Reset form fields
                setName('');
                setUserEmail('');
                setMessage('');
            } else {
                // Error from API
                setStatus({
                    show: true,
                    error: true,
                    message: `ERROR: ${data.message || 'Transmission failed. Please try again later.'}`
                });
            }
        } catch (error) {
            // Network error or other exceptions
            setStatus({
                show: true,
                error: true,
                message: 'ERROR: System failure during transmission. Please try again later.'
            });
        } finally {
            // Stop loading state
            setSubmitting(false);
        }
    };

    return (
        <>
            <PageMeta
                title="Contact"
                description="Get in touch for collaboration, project inquiries, or questions about audio plugins and creative development."
                canonicalPath="/contact"
            />
            <Terminal>
                <TerminalHeader />
                <TerminalContent>
                    {/* Previously completed commands */}
                    {history.map((item, index) => (
                        <React.Fragment key={`history-${index}`}>
                            <CommandLine>
                                <Prompt>root@devSystem:~$</Prompt>
                                <Command>{item.command}</Command>
                            </CommandLine>
                            <Response>{item.response}</Response>
                        </React.Fragment>
                    ))}

                    {/* Current command being typed */}
                    {!showForm && currentText && (
                        <CommandLine>
                            <Prompt>root@devSystem:~$</Prompt>
                            <Command>{currentText}</Command>
                        </CommandLine>
                    )}

                    {/* Current response */}
                    {showResponse && !showForm && (
                        <Response>
                            {responseSequence[commandIndexRef.current]}
                        </Response>
                    )}

                    {/* Form */}
                    {showForm && (
                        <Form onSubmit={handleSubmit}>
                            <InputLine>
                                <InputPrompt htmlFor="contact-name">NAME:&gt;</InputPrompt>
                                <InputField
                                    id="contact-name"
                                    ref={nameInputRef}
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    required
                                />
                            </InputLine>

                            <InputLine>
                                <InputPrompt htmlFor="contact-email">EMAIL:&gt;</InputPrompt>
                                <InputField
                                    id="contact-email"
                                    type="email"
                                    value={userEmail}
                                    onChange={(e) => setUserEmail(e.target.value)}
                                    required
                                />
                            </InputLine>

                            <InputLine style={{ alignItems: 'flex-start' }}>
                                <InputPrompt htmlFor="contact-message">MESSAGE:&gt;</InputPrompt>
                                <TextareaField
                                    id="contact-message"
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    required
                                />
                            </InputLine>

                            <StyledButton
                                type="submit"
                                disabled={submitting}
                            >
                                {submitting ? 'TRANSMITTING...' : 'TRANSMIT MESSAGE'}
                            </StyledButton>

                            <StatusMessage
                                visible={status.show}
                                error={status.error}
                                role="status"
                                aria-live="polite"
                                aria-atomic="true"
                            >
                                {status.message}
                            </StatusMessage>
                        </Form>
                    )}
                </TerminalContent>
            </Terminal>
        </>
    );
};

export default ContactPage;